import { useCallback, useEffect, useRef, useState } from "react"

/**
 * The always-visible horizontal scrollbar, matching the design system's slim
 * bar: an 8px full-width muted track with a translucent thumb sized to the
 * visible fraction of the table.
 *
 * It is drawn rather than delegated to the browser because macOS overlay
 * scrollbars fade out when idle, so the native bar is invisible most of the
 * time — the design wants a persistent one.
 */

export type ThumbGeometry = { widthPct: number; leftPct: number }

/** Thumb size and offset as percentages of the track. */
export const resolveThumb = ({
  scrollLeft,
  scrollWidth,
  clientWidth,
  minWidthPct = 8,
}: {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
  minWidthPct?: number
}): ThumbGeometry => {
  if (scrollWidth <= clientWidth || scrollWidth === 0) {
    return { widthPct: 100, leftPct: 0 }
  }
  // Keep the thumb grabbable on very wide tables.
  const widthPct = Math.max((clientWidth / scrollWidth) * 100, minWidthPct)
  const maxLeftPct = 100 - widthPct
  const progress = scrollLeft / (scrollWidth - clientWidth)
  return {
    widthPct,
    leftPct: Math.min(Math.max(progress * maxLeftPct, 0), maxLeftPct),
  }
}

export const AppTableScrollbar = ({
  getScroller,
  top,
}: {
  /** Returns the element antd is actually scrolling. */
  getScroller: () => HTMLElement | null
  /**
   * Offset from the top of the table wrap, measured by AppTable so the bar sits
   * straight under the rows. antd draws the paginator inside the same element as
   * the rows, so a bar placed after the table would land below the paginator.
   */
  top?: number
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(
    null
  )
  const [visible, setVisible] = useState(false)
  const [thumb, setThumb] = useState<ThumbGeometry>({
    widthPct: 100,
    leftPct: 0,
  })

  const sync = useCallback(() => {
    const el = getScroller()
    if (!el) return
    const scrollable = el.scrollWidth - el.clientWidth > 1
    setVisible((prev) => (prev === scrollable ? prev : scrollable))
    if (!scrollable) return

    const next = resolveThumb({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    })
    // Same guard as the edge bands: a new object every scroll event would
    // re-trigger the effect that registered the listener.
    setThumb((prev) =>
      prev.widthPct === next.widthPct && prev.leftPct === next.leftPct
        ? prev
        : next
    )
  }, [getScroller])

  useEffect(() => {
    const el = getScroller()
    if (!el) return undefined

    el.addEventListener("scroll", sync, { passive: true })
    sync()

    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sync)
      observer.observe(el)
      const table = el.querySelector("table")
      if (table) observer.observe(table)
    }

    return () => {
      el.removeEventListener("scroll", sync)
      observer?.disconnect()
    }
  }, [getScroller, sync])

  // Dragging is tracked on the window so the pointer can leave the thumb
  // mid-drag without the bar sticking.
  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      const drag = dragRef.current
      const el = getScroller()
      const track = trackRef.current
      if (!drag || !el || !track) return

      const trackWidth = track.clientWidth
      const maxScroll = el.scrollWidth - el.clientWidth
      const travel = trackWidth * (1 - thumb.widthPct / 100)
      if (travel <= 0) return

      const delta = event.clientX - drag.startX
      el.scrollLeft = drag.startScrollLeft + (delta / travel) * maxScroll
    }
    const onUp = () => {
      dragRef.current = null
      document.body.style.userSelect = ""
    }

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
  }, [getScroller, thumb.widthPct])

  if (!visible) return null

  return (
    <div
      ref={trackRef}
      className="app-table-scrollbar"
      style={top === undefined ? undefined : { top }}
      onMouseDown={(event) => {
        // Clicking the track jumps to that position.
        if (event.target !== trackRef.current) return
        const el = getScroller()
        const track = trackRef.current
        if (!el || !track) return
        const rect = track.getBoundingClientRect()
        const ratio = (event.clientX - rect.left) / rect.width
        el.scrollLeft = ratio * (el.scrollWidth - el.clientWidth)
      }}
    >
      <div
        className="app-table-scrollbar__thumb"
        style={{ width: `${thumb.widthPct}%`, left: `${thumb.leftPct}%` }}
        onMouseDown={(event) => {
          const el = getScroller()
          if (!el) return
          event.preventDefault()
          dragRef.current = {
            startX: event.clientX,
            startScrollLeft: el.scrollLeft,
          }
          document.body.style.userSelect = "none"
        }}
        role="presentation"
      />
    </div>
  )
}

export default AppTableScrollbar
